import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FileText,
  Download,
  Home,
  Menu,
  Folder,
  LogOut,
  Sparkles,
  ShieldCheck,
  Wallet,
  Command as CommandIcon,
  MessageSquare,
  History,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import styles from './Layout.module.css'
import CommandMenu, { CommandAction } from './CommandMenu'

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [commandOpen, setCommandOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const navigation = useMemo(
    () => [
      { name: 'Overview', href: '/dashboard', icon: Home },
      { name: 'Transcripts', href: '/extract', icon: FileText },
      { name: 'Chat', href: '/chat', icon: Folder },
      { name: 'Library', href: '/files', icon: Download },
      { name: 'Billing', href: '/billing', icon: Wallet },
      { name: 'Account', href: '/account', icon: ShieldCheck },
    ],
    []
  )

  const handleLogout = useCallback(() => {
    logout()
    navigate('/login', { replace: true })
  }, [logout, navigate])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setCommandOpen((value) => !value)
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const originalOverflow = document.body.style.overflow

    if (sidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [sidebarOpen])

  const renderCommandIcon = useCallback(
    (Icon: LucideIcon): CommandAction['icon'] =>
      ({ size, className }) => <Icon size={size} className={className} />,
    []
  )

  const commandActions = useMemo<CommandAction[]>(
    () => [
      ...navigation.map((item) => ({
        id: item.href,
        label: item.name,
        description: `Go to ${item.name} workspace`,
        icon: renderCommandIcon(item.icon),
        group: 'Navigation',
        onSelect: () => navigate(item.href),
      })),
      {
        id: 'history',
        label: 'Review processing history',
        description: 'Check batch progress, rerun failures and export archives.',
        icon: renderCommandIcon(History),
        group: 'Workflow',
        onSelect: () => navigate('/history'),
      },
      {
        id: 'downloads',
        label: 'Open download manager',
        description: 'Generate new exports or retrieve previous downloads.',
        icon: renderCommandIcon(Download),
        group: 'Workflow',
        onSelect: () => navigate('/downloads'),
      },
      {
        id: 'chat-latest',
        label: 'Continue recent conversation',
        description: 'Jump straight into transcript Q&A and knowledge tools.',
        icon: renderCommandIcon(MessageSquare),
        group: 'Workflow',
        onSelect: () => navigate('/chat'),
      },
      {
        id: 'logout',
        label: 'Sign out securely',
        description: 'Log out of Transcripter across all devices.',
        icon: renderCommandIcon(LogOut),
        group: 'Account',
        onSelect: handleLogout,
      },
    ],
    [handleLogout, navigate, navigation, renderCommandIcon]
  )

  const currentNav = navigation.find((item) => location.pathname.startsWith(item.href))
  const userInitials = user ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() : 'U'
  const creditBalance = user?.creditBalance ?? 0

  return (
    <div className={styles.wrapper}>
      {sidebarOpen && (
        <div
          className={styles.overlay}
          role="presentation"
          aria-hidden="true"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={cn(styles.sidebar, sidebarOpen && styles.sidebarOpen)}>
        <div className={styles.brand}>
          <div className={styles.brandLogo}>
            <Sparkles size={20} />
          </div>
          <div className={styles.brandText}>
            <span className={styles.brandTitle}>Transcripter</span>
            <span className={styles.brandSubtitle}>Conversational transcript studio</span>
          </div>
        </div>

        <div className={styles.navSection}>
          <span className={styles.navLabel}>Workspace</span>
          <ul className={styles.navList}>
            {navigation.map((item) => {
              const isActive = location.pathname.startsWith(item.href)
              const Icon = item.icon
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className={cn(styles.navLink, isActive && styles.navLinkActive)}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className={styles.navIcon} />
                    <span>{item.name}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>

        <div className={styles.sidebarFooter}>
          {user && (
            <div className={styles.userCard}>
              <div className={styles.userAvatar}>{userInitials}</div>
              <div className={styles.userMeta}>
                <span className={styles.userName}>
                  {user.firstName} {user.lastName}
                </span>
                <span className={styles.userEmail}>{user.email}</span>
              </div>
            </div>
          )}
          <Button variant="outline" onClick={handleLogout}>
            <LogOut size={16} />
            Logout
          </Button>
          <Button
            variant="ghost"
            onClick={() => window.dispatchEvent(new CustomEvent('cookie-consent:open'))}
          >
            Cookie preferences
          </Button>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.topBar}>
          <div className={styles.topActions}>
            <Button
              variant="outline"
              size="icon"
              className={styles.mobileToggle}
              onClick={() => setSidebarOpen(true)}
              aria-label="Toggle navigation"
            >
              <Menu size={18} />
            </Button>
            <h2>{currentNav?.name ?? 'Dashboard'}</h2>
            <span className={styles.creditChip}>
              <Sparkles size={14} />
              {creditBalance} credits left
            </span>
          </div>

          <div className={cn(styles.topActions, styles.topActionsDesktop)}>
            <Button
              variant="ghost"
              className={styles.commandButton}
              onClick={() => setCommandOpen(true)}
            >
              <CommandIcon size={16} />
              Quick command
              <span aria-hidden="true" className={styles.commandShortcut}>
                <kbd>⌘</kbd>
                <kbd>K</kbd>
              </span>
            </Button>
            <Button onClick={() => navigate('/extract')}>Start Extraction</Button>
            <Button variant="secondary" onClick={() => navigate('/chat')}>
              Go to Chats
            </Button>
          </div>
        </header>

        <main className={styles.content}>
          <Outlet />
        </main>

        <nav className={styles.mobileActionBar} aria-label="Quick actions">
          <Button onClick={() => navigate('/extract')}>Start extraction</Button>
          <Button variant="secondary" onClick={() => navigate('/history')}>
            View history
          </Button>
        </nav>
      </div>
      <CommandMenu open={commandOpen} onClose={() => setCommandOpen(false)} actions={commandActions} />
    </div>
  )
}

export default Layout
